import type {
  BatchBlockReason,
  MeasurementVerdict,
  MonthlyReportEmailStatus,
  MonthlyReportStatus,
  PipelineStage,
  PipelineTag,
  PublishEffectName,
  PublishEffectStatus,
  RecapEntryKind,
  RecapPeriod,
  RecommendationSafetyClass,
  RecommendationStatus,
  RecommendationType,
} from "@siteagain/shared-types";

import type { Locale } from "../i18n";

/**
 * Every word Mission Control shows. Codes the API returns (stages, statuses, block reasons)
 * are translated here, never displayed raw: an operator should not have to read `not_safe_class`.
 */
const EN = {
  nav: {
    today: "Today", fleet: "Fleet", reports: "Reports", pilots: "Pilots", resources: "Resources", queue: "Queue", aria: "Mission Control navigation",
    badge: {
      today: (count: number) => `${count} ${count === 1 ? "change" : "changes"} waiting for approval`,
      fleet: (count: number) => `${count} open ${count === 1 ? "incident" : "incidents"}`,
      reports: (count: number) => `${count} ${count === 1 ? "report" : "reports"} to review`,
    },
  },
  header: {
    eyebrowOps: "SiteAgain operations",
    eyebrowPilots: "SiteAgain acquisition",
    title: "Mission Control",
    pilotsTitle: "Pilot inbox",
    refresh: "Refresh",
    exit: "Customer studio",
    language: "Language",
    refreshNow: "Refresh now",
  },
  account: { menu: "Account menu", language: "Language", english: "English", french: "Français", signOut: "Sign out" },
  status: {
    loading: "Loading…",
    live: (ago: string) => `Updated ${ago}`,
    updating: "Updating…",
    stale: (ago: string) => `Out of date · last update ${ago}`,
    partial: (ago: string | null) => (ago ? `Part of this screen could not refresh · last full update ${ago}` : "Part of this screen could not refresh"),
    offline: (ago: string | null) => (ago ? `Offline · last update ${ago}` : "Offline · nothing loaded yet"),
  },
  scope: { all: "All sites", sites: "Sites", noSites: "No sites yet." },
  period: { "24h": "24 hours", "7d": "7 days", "30d": "30 days" } satisfies Record<RecapPeriod, string>,
  recap: {
    title: "What happened",
    since: (period: string) => `Last ${period}`,
    shipped: "Went live",
    awaiting: "Waiting for approval",
    awaitingDetail: "right now",
    failed: "Stopped on an error",
    rolledBack: "Rolled back",
    measured: "Measured",
    measuredPartial: (improved: number, total: number) => `${improved} of ${total} improved`,
    audits: "Audits completed",
    newRecommendations: "New recommendations",
    empty: "Nothing happened in this period.",
    emptyFiltered: "Nothing of this kind in this period.",
    journal: "Journal",
    filtering: (label: string) => `Showing: ${label}`,
    clearFilter: "Show everything",
    showMore: (count: number) => `Show ${count} more`,
    showLess: "Show less",
    jumpHint: { approval: "Go to the changes waiting for approval", failed: "Go to what stopped on an error", filter: "Show only these in the journal" },
    entry: {
      deployed: "went live",
      rolled_back: "was rolled back",
      preview_ready: "preview is ready for approval",
      rejected: "preview was declined",
      failed: "stopped on an error",
      measured: "measured",
      audit_completed: "audit completed",
      audit_failed: "audit failed",
    } satisfies Record<RecapEntryKind, string>,
    measuredAfter: (days: number, verdict: string) => `after ${days} days: ${verdict}`,
  },
  verdict: {
    improved: "improved",
    neutral: "no clear change",
    declined: "declined",
    insufficient: "not enough data",
  } satisfies Record<MeasurementVerdict, string>,
  pipeline: {
    title: "Work in progress",
    subtitle: "Each card is a recommendation or a change, in the stage it is really in.",
    stage: {
      detected: "Detected",
      saved: "Saved",
      building: "Building",
      to_approve: "To approve",
      deployed: "Live",
      measured: "Measured",
    } satisfies Record<PipelineStage, string>,
    hint: {
      detected: "Found by an audit",
      saved: "Kept for later",
      building: "Preview being built",
      to_approve: "Preview ready: needs a decision",
      deployed: "Live on the site",
      measured: "Checked after 7, 14 and 28 days",
    } satisfies Record<PipelineStage, string>,
    detectedFilter: { all: "All", new: "New", saved: "Saved" },
    detectedHint: "Found by an audit, or kept for later",
    declined: "Declined",
    tag: { technical: "Technical", content: "Content", schema: "Schema", performance: "Performance" } satisfies Record<PipelineTag, string>,
    safety: { safe_draft: "Low risk", review_required: "Needs review", expert_required: "Expert only", not_applicable: "Not applicable" } satisfies Record<RecommendationSafetyClass, string>,
    empty: "Nothing here.",
    more: (hidden: number) => `+ ${hidden} more`,
    failedChip: "Stopped",
    stoppedOnError: "Stopped on an error",
    noErrorMessage: "No error message was recorded.",
    manualChange: "Requested change",
    changeStatus: {
      requested: "Queued", planning: "Planning", editing: "Editing", validating: "Checking", preview_ready: "Preview ready",
      awaiting_approval: "Awaiting approval", approved: "Approved", deploying: "Publishing", deployed: "Live",
      rejected: "Declined", failed: "Failed", rolled_back: "Rolled back",
    } as Record<string, string>,
    updated: (ago: string) => `updated ${ago}`,
    live: (ago: string) => `live ${ago}`,
    allChecksPassed: "All checks passed",
    blockedBy: (count: number) => `${count} ${count === 1 ? "check" : "checks"} need a person`,
  },
  action: {
    buildPreview: "Build preview",
    save: "Save",
    dismiss: "Dismiss",
    approve: "Approve",
    reject: "Decline",
    rollback: "Roll back",
    rollbackQuiet: "Roll back…",
    retry: "Try again",
    details: "View details",
    openPreview: "Open preview",
    confirm: "Confirm",
    cancel: "Cancel",
    working: "Working…",
    close: "Close",
    runAudit: "Run audit now",
    open: "Open",
    inspect: (name: string) => `Inspect ${name}`,
  },
  notice: {
    approved: "Approved. It is being published.",
    rejected: "Declined. Nothing was published.",
    rollbackStarted: "Rollback started. The previous version is being restored.",
    previewStarted: "A preview is being built. Nothing is published until it is approved.",
    saved: "Saved for later.",
    dismissed: "Dismissed. A later audit can raise it again with fresh evidence.",
    auditRequested: "Audit requested. The result arrives shortly.",
    batch: (approved: number, skipped: number) => `${approved} approved${skipped > 0 ? `, ${skipped} left for individual review` : ""}.`,
    reportApproved: "Report approved.",
    reportSuppressed: "Report withheld. The customer will not see it.",
    failed: (message: string) => `Could not complete the action: ${message}`,
  },
  confirm: {
    rollback: "Roll this change back? The previous version goes live again.",
    batch: (count: number) => `Approve ${count} ${count === 1 ? "change" : "changes"}? Each keeps its own preview, evidence and rollback.`,
  },
  batch: {
    ready: (count: number) => `${count} low-risk ${count === 1 ? "change is" : "changes are"} ready`,
    approveAll: "Approve all",
    explain: "Only changes whose every check passed and whose preview you have not changed are included.",
    reason: {
      not_found: "No longer exists",
      not_awaiting_approval: "Already decided",
      no_recommendation: "Not raised by a recommendation",
      not_safe_class: "Needs a person to review it",
      not_applicable: "Cannot be applied automatically",
      no_evidence: "Evidence is missing",
      seo_findings: "SEO checks raised findings",
      build_warnings: "The build raised warnings",
      broken_links_or_assets: "Broken links or missing assets",
      no_visual_evidence: "No visual comparison",
      visual_change_too_large: "The page looks noticeably different",
      evidence_changed: "The evidence changed since you looked",
    } satisfies Record<BatchBlockReason, string>,
  },
  attention: {
    title: "Needs attention",
    stopped: (ago: string) => `Stopped ${ago}`,
    incidents: (count: number) => `${count} open ${count === 1 ? "incident" : "incidents"} in Fleet`,
    manualHint: "Made by hand: the customer starts it again.",
  },
  fleet: {
    index: "Fleet index",
    allWebsites: "All websites",
    search: "Search site, domain, owner…",
    filters: { all: "all", critical: "critical", watch: "watch", building: "building", healthy: "healthy" } as Record<string, string>,
    columns: { website: "Website", health: "Health", work: "Work", seo: "SEO", usage: "Usage MTD", last: "Last signal", report: "Report" },
    noMatch: "No websites match this view.",
    clear: "Clear filters",
    attention: "Human-in-the-loop",
    queue: "Attention queue",
    queueClear: "Queue clear",
    queueClearDetail: "No website needs operator intervention.",
    take: "Take ownership",
    resolve: "Resolve",
    ownedBy: (who: string) => `Owned by ${who}`,
    you: "you",
    systemTitle: "System & activity",
    systemHint: (jobs: number, workflows: number) => `${jobs} ${jobs === 1 ? "job" : "jobs"} running · ${workflows} active ${workflows === 1 ? "workflow" : "workflows"}`,
    resourcesTitle: "Capacity & throughput",
    resourcesDetail: "Operational inventory across the entire fleet.",
    logTitle: "Recent activity",
    logDetail: "Newest state transitions across every resource.",
    websites: "Websites",
    needAttention: "Need attention",
    liveCount: (count: number) => `${count} live`,
    incidents: (count: number) => `${count} open ${count === 1 ? "incident" : "incidents"}`,
    activeWorkflows: "Active workflows",
    jobsRunning: (count: number) => `${count} ${count === 1 ? "job" : "jobs"} running`,
    toApprove: (count: number) => `${count} to approve`,
    monthUsage: "Month usage",
    today: (amount: string) => `${amount} today`,
    approvals: "Waiting for approval",
    reviews: "Reports to review",
    workIdle: "Idle",
    workActive: (count: number) => `${count} active`,
    scoreNone: "Not assessed",
    scoreBehind: "Changes went live after this audit",
    scoreCaptured: (ago: string) => `audit ${ago}`,
    recommendations: (count: number) => `${count} to decide`,
    footerSnapshot: "Snapshot",
    resourceJobs: "Queue jobs",
    resourceDeployments: "Deployments",
    resourceAudits: "SEO audits",
    resourceRecommendations: "Recommendations",
    usageMonth: "month to date",
    onboardButton: "Add website",
    onboardModal: {
      title: "Add a website",
      subtitle: "Paste its address. The first audit starts straight away and opens here when it is ready.",
      nameLabel: "Name",
      namePlaceholder: "Acme Heating",
      nameHint: "Filled in from the address. Change it if you like.",
      urlLabel: "Website address",
      urlPlaceholder: "acmeheating.co.uk",
      urlInvalid: "That does not look like a website address.",
      goalLabel: "What matters most to them?",
      goals: {
        leads: "Leads & Enquiries",
        traffic: "Search & AI Visibility",
        maintenance: "Maintenance & Savings",
      },
      goalHints: {
        leads: "More people get in touch",
        traffic: "Found on Google and in AI answers",
        maintenance: "Fast, safe, cheaper to run",
      },
      localeLabel: "Language",
      ownerEmailLabel: "Owner email",
      ownerEmailPlaceholder: "owner@acmeheating.co.uk",
      moreOptions: "More options",
      nextTitle: "What happens next",
      next: ["We audit the live site. It stays untouched.", "You read the audit and pick what to improve.", "Each change is previewed. Nothing goes live without approval."],
      submitButton: "Add & start audit",
      cancelButton: "Cancel",
      submitting: "Adding…",
      successNotice: "Website added. The first audit is running and opens here when it is ready.",
    },
  },
  activity: {
    kind: { job: "Job", deployment: "Deployment", seo_audit: "SEO audit", recommendation: "Recommendation", change_set: "Change" } as Record<string, string>,
    audit: { baseline: "Baseline SEO audit", weekly: "Weekly SEO audit", measurement: "Measurement audit", manual: "On-demand SEO audit" } as Record<string, string>,
    environment: { preview: "Preview", production: "Production" } as Record<string, string>,
    task: {
      light_audit: "Assessment run",
      full_site_audit: "Full-site audit",
      start_migration: "Migration started",
      crawl_site: "Site crawled",
      capture_page: "Page captured",
      generate_project: "Site generated",
      build_project: "Site built",
      deploy_preview: "Preview deployed",
      import_repository_baseline: "Repository imported",
    } as Record<string, string>,
    status: {
      running: "running", pending: "queued", completed: "completed", failed: "failed", dead_lettered: "failed",
      active: "live", superseded: "replaced", building: "building", publishing: "publishing",
      queued: "queued", partial: "partial", rolled_back: "rolled back", deployed: "live",
      awaiting_approval: "awaiting approval", rejected: "declined", new: "new", applied: "applied", measuring: "measuring",
      measured: "measured", skipped: "dismissed", previewing: "building preview", previewed: "preview ready",
    } as Record<string, string>,
  },
  health: { healthy: "Healthy", building: "Building", watch: "Watch", critical: "Critical" } as Record<string, string>,
  drawer: {
    title: "Website telemetry",
    production: "Production",
    notConnected: "Not connected",
    owner: "Owner",
    systemOwned: "System owned",
    live: "Live version",
    lastChange: (ago: string) => `Last change ${ago}`,
    noChange: "Nothing has been published yet",
    score: "SEO score",
    scoreLine: (score: number, ago: string) => `${Math.round(score)} / 100 · audit ${ago}`,
    scoreBehind: "Changes went live after this audit, so the score may not include them. A fresh audit is requested after each publish.",
    noScore: "No audit has completed yet",
    changes: "Changes",
    noChanges: "No changes yet.",
    recommendations: "Recommendations",
    noRecommendations: "No recommendations yet.",
    measurements: "Measured results",
    noMeasurements: "Nothing has been measured yet. A result appears 7, 14 and 28 days after a change goes live.",
    audits: "Audit history",
    noAudits: "No audits yet.",
    effects: "After publishing",
    noEffects: "Nothing has been published since this was recorded.",
    reports: "Monthly reports",
    noReports: "No monthly report yet. The first one is written after a full calendar month.",
    search: "Search Console",
    noSearch: "Not connected. The customer connects it from their Growth view.",
    searchStatus: { pending_property: "Choosing a property", active: "Connected", needs_reconnect: "Needs reconnecting" } as Record<string, string>,
    searchProperty: "Property",
    searchAccount: "Google account",
    searchThrough: (day: string) => `Numbers through ${day}`,
    searchNoNumbers: "No numbers pulled yet",
    searchLastPull: (ago: string) => `Last pull ${ago}`,
    searchRuns: "Recent pulls",
    searchNoRuns: "Nothing pulled yet.",
    plan: {
      title: "Plan and full build",
      none: "No plan chosen yet. The customer chooses one after their quick proof.",
      status: { requested: "Requested", contacted: "Contacted", activated: "Active", cancelled: "Cancelled" } as Record<string, string>,
      requestedAt: (when: string) => `Requested ${when}`,
      activate: "Billing is set up: activate the plan",
      activateHint: "Activate only once billing is in place. It unlocks the full build for this site.",
      scope: "Scope",
      fullSite: "Every page",
      homepage: "Homepage only",
      start: "Start the full build",
      startHint: "Runs the complete migration with every quality gate. The customer's live site is untouched.",
      latest: (status: string, when: string) => `Latest build: ${status}, started ${when}`,
      running: "A build is already in progress for this site.",
    },
    enquiries: "Enquiries",
    noEnquiries: "Nothing has reached this site's form yet. The customer connects it from their Growth view. What visitors write is the customer's alone: operators see counts only.",
    enquiryLast: "Last 28 days",
    enquiryPrevious: "The 28 days before",
    enquiryNoPrevious: "Not listening yet for that whole period",
    enquirySince: (day: string) => `Counting since ${day}`,
    enquiryLastAt: (ago: string) => `Last one ${ago}`,
    enquiryEmailProblems: (failed: number, skipped: number) => [failed > 0 ? `${failed} owner ${failed === 1 ? "email" : "emails"} failed` : "", skipped > 0 ? `${skipped} not emailed` : ""].filter(Boolean).join(" · ") + " in the last 30 days",
    enquiryResults: "Measured against changes",
    enquiryResult: (before: number, after: number) => `${before} → ${after} enquiries`,
    enquiryVerdict: { improved: "More", neutral: "About the same", declined: "Fewer", insufficient: "Not enough" } as Record<string, string>,
    runKind: { backfill: "Backfill", refresh: "Refresh" } as Record<string, string>,
    runStatus: { running: "Running", done: "Done", failed: "Failed" } as Record<string, string>,
    runRows: (count: number) => `${count} rows`,
    runTruncated: "capped at the row limit",
    problem: {
      token_revoked: "Access withdrawn by the customer",
      access_lost: "The Google account lost access to the property",
      property_missing: "The property no longer exists",
      provider_unavailable: "Google did not answer",
      rate_limited: "Google asked us to slow down",
      missing_scope: "The read permission was not granted",
    } as Record<string, string>,
    effectName: { refresh_audit: "Score refreshed", notify_owner: "Owner told", notify_search_engines: "Search engines told" } satisfies Record<PublishEffectName, string>,
    effectStatus: { pending: "Waiting", running: "Running", done: "Done", skipped: "Not needed", failed: "Failed" } satisfies Record<PublishEffectStatus, string>,
    recommendationStatus: {
      new: "New", viewed: "Seen", saved: "Saved", skipped: "Dismissed", previewing: "Building preview", previewed: "Preview ready",
      applied: "Live", expired: "Expired", invalidated: "Withdrawn", measuring: "Measuring", measured: "Measured",
    } satisfies Record<RecommendationStatus, string>,
    type: {
      metadata: "Titles & descriptions", schema: "Structured data", internal_link: "Internal links", content: "Content",
      technical: "Technical", performance: "Performance", structural: "Structure", new_page: "New page", redirect: "Redirect",
    } satisfies Record<RecommendationType, string>,
    loadFailed: "Could not load this website.",
  },
  reports: {
    title: "Monthly reports",
    subtitle: "What was done for each website, one calendar month at a time.",
    awaiting: "Waiting for review",
    history: "History",
    none: "No monthly report has been written yet. The first appears after a full calendar month.",
    autoApproval: (when: string) => `If nobody acts, it is approved and sent automatically from ${when} (checked daily).`,
    approve: "Approve & send",
    suppress: "Withhold",
    note: "Note (optional)",
    columns: { site: "Website", month: "Month", status: "Status", email: "Email", shipped: "Live", rolledBack: "Rolled back", measured: "Measured" },
    status: { draft: "Draft", approved: "Approved", suppressed: "Withheld" } satisfies Record<MonthlyReportStatus, string>,
    email: { pending: "Email pending", sent: "Emailed", skipped: "Not emailed", failed: "Email failed" } satisfies Record<MonthlyReportEmailStatus, string>,
    quiet: "Quiet month",
    reviewedBy: (who: string, when: string) => `Reviewed by ${who} ${when}`,
    autoReviewed: (when: string) => `Approved automatically ${when}`,
    loadFailed: "Could not load the report.",
    review: "Review",
    reviewHint: "Read the report below exactly as the customer will see it. Approving sends the email at once when the month is not quiet.",
    sentTo: "Emailed",
    quietNote: "Quiet months are stored and visible in the app, but never emailed.",
    close: "Close report",
    suppressConfirm: "Withhold this report? The customer will never see it.",
    noneAwaiting: "Nothing is waiting for review.",
    withheldBy: (who: string, when: string) => `Withheld by ${who} ${when}`,
  },
  today: {
    greeting: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" },
    needsYou: (count: number) => (count === 0 ? "Nothing needs you right now." : `${count} ${count === 1 ? "thing needs" : "things need"} you.`),
    calm: "Audits, previews and measurements keep running in the background.",
    queueTitle: "Needs you",
    queueEmpty: "You are all caught up.",
    kind: { stopped: "Stopped on an error", approve: "Ready to approve", report: "Monthly report to review", audit: "New audit ready" },
    auditReady: (count: number) => `${count} ${count === 1 ? "recommendation" : "recommendations"} to decide`,
    flowTitle: "Work in progress",
    flowEmpty: "Nothing at this stage.",
    recentTitle: "Recently",
    firstSiteTitle: "Add your first website",
    firstSiteBody: "Paste an address. SiteAgain audits it, explains what to improve, and nothing goes live without your approval.",
    more: "More",
    showAll: (count: number) => `Show all ${count}`,
  },
  audit: {
    eyebrow: "SEO & GEO audit",
    back: "Back",
    open: "Open audit",
    openReport: "Open the audit",
    latest: "Latest audit",
    history: "Earlier audits",
    running: "Audit running",
    runningBody: "Usually a minute or two. The report opens here when it is ready.",
    failed: "This audit did not complete",
    failedBody: "No report was stored for this run. Run a new one; the live site is untouched either way.",
    notAssessed: "Not assessed yet",
    notAssessedBody: "Run an audit to see where this website stands.",
    runAgain: "Run a new audit",
    outOf: "/ 100",
    grade: (grade: string) => `Grade ${grade}`,
    captured: (when: string) => `Captured ${when}`,
    purpose: { baseline: "Baseline audit", weekly: "Weekly audit", measurement: "Measurement audit", manual: "On-demand audit" } as Record<string, string>,
    partial: "Partial report: some checks did not return. Everything below is what did.",
    components: "Score breakdown",
    todo: "What to do next",
    todoHint: "Each step is built as a private preview first. Nothing goes live without approval.",
    todoEmpty: "This audit raised no recommendations. That is a valid result.",
    findings: "What the audit found",
    findingsEmpty: "No findings were recorded.",
    severity: { critical: "Critical", warning: "Warning", opportunity: "Opportunity", info: "Info" } as Record<string, string>,
    impact: { high: "High impact", medium: "Medium impact", low: "Low impact" } as Record<string, string>,
    remediation: "How to fix it",
    affected: (count: number) => `${count} ${count === 1 ? "page" : "pages"}`,
    keywords: "Search queries",
    keywordsHint: "Where the site appears today, as reported by the provider.",
    columns: { query: "Query", position: "Position", impressions: "Impressions", clicks: "Clicks", volume: "Volume" },
    visibility: "Authority & AI visibility",
    aiCitation: "AI citation share",
    mentions: "AI mentions",
    referringDomains: "Referring domains",
    authority: "Domain authority",
    rankedKeywords: "Ranked keywords",
    limitations: "What this audit cannot tell you",
    loadFailed: "Could not load this audit.",
    ready: (site: string) => `The audit for ${site} is ready.`,
  },
  pulse: { aria: "Data freshness" },
  empty: { board: "No work yet. Run an audit on a website to see recommendations here." },
};

export type OperatorCopy = typeof EN;

const FR: OperatorCopy = {
  nav: {
    today: "Aujourd'hui", fleet: "Parc", reports: "Rapports", pilots: "Pilotes", resources: "Ressources", queue: "File", aria: "Navigation Mission Control",
    badge: {
      today: (count) => `${count} ${count === 1 ? "modification attend" : "modifications attendent"} une validation`,
      fleet: (count) => `${count} ${count === 1 ? "incident ouvert" : "incidents ouverts"}`,
      reports: (count) => `${count} ${count === 1 ? "rapport à relire" : "rapports à relire"}`,
    },
  },
  header: {
    eyebrowOps: "Opérations SiteAgain",
    eyebrowPilots: "Acquisition SiteAgain",
    title: "Mission Control",
    pilotsTitle: "Boîte des pilotes",
    refresh: "Actualiser",
    exit: "Espace client",
    language: "Langue",
    refreshNow: "Actualiser maintenant",
  },
  account: { menu: "Menu du compte", language: "Langue", english: "English", french: "Français", signOut: "Se déconnecter" },
  status: {
    loading: "Chargement…",
    live: (ago) => `Mis à jour ${ago}`,
    updating: "Mise à jour…",
    stale: (ago) => `Données anciennes · dernière mise à jour ${ago}`,
    partial: (ago) => (ago ? `Une partie de l'écran n'a pas pu s'actualiser · dernière mise à jour complète ${ago}` : "Une partie de l'écran n'a pas pu s'actualiser"),
    offline: (ago) => (ago ? `Hors ligne · dernière mise à jour ${ago}` : "Hors ligne · rien de chargé"),
  },
  scope: { all: "Tous les sites", sites: "Sites", noSites: "Aucun site pour l'instant." },
  period: { "24h": "24 heures", "7d": "7 jours", "30d": "30 jours" },
  recap: {
    title: "Ce qui s'est passé",
    since: (period) => `Sur ${period}`,
    shipped: "Mises en ligne",
    awaiting: "Attendent une validation",
    awaitingDetail: "en ce moment",
    failed: "Arrêtées sur une erreur",
    rolledBack: "Annulées",
    measured: "Mesurées",
    measuredPartial: (improved, total) => `${improved} sur ${total} en progrès`,
    audits: "Audits terminés",
    newRecommendations: "Nouvelles recommandations",
    empty: "Rien ne s'est passé sur cette période.",
    emptyFiltered: "Rien de ce type sur cette période.",
    journal: "Journal",
    filtering: (label) => `Filtre : ${label}`,
    clearFilter: "Tout afficher",
    showMore: (count) => `Voir ${count} de plus`,
    showLess: "Réduire",
    jumpHint: { approval: "Aller aux modifications qui attendent une validation", failed: "Aller à ce qui s'est arrêté sur une erreur", filter: "Ne montrer que celles-ci dans le journal" },
    entry: {
      deployed: "est en ligne",
      rolled_back: "a été annulée",
      preview_ready: "l'aperçu attend une validation",
      rejected: "l'aperçu a été refusé",
      failed: "s'est arrêtée sur une erreur",
      measured: "mesurée",
      audit_completed: "audit terminé",
      audit_failed: "audit échoué",
    },
    measuredAfter: (days, verdict) => `après ${days} jours : ${verdict}`,
  },
  verdict: {
    improved: "en progrès",
    neutral: "pas de changement net",
    declined: "en recul",
    insufficient: "données insuffisantes",
  },
  pipeline: {
    title: "Travaux en cours",
    subtitle: "Chaque carte est une recommandation ou une modification, à l'étape où elle en est vraiment.",
    stage: {
      detected: "Détecté",
      saved: "Gardé",
      building: "En préparation",
      to_approve: "À valider",
      deployed: "En ligne",
      measured: "Mesuré",
    },
    hint: {
      detected: "Trouvé par un audit",
      saved: "Mis de côté",
      building: "Aperçu en construction",
      to_approve: "Aperçu prêt : décision attendue",
      deployed: "En ligne sur le site",
      measured: "Vérifié après 7, 14 et 28 jours",
    },
    detectedFilter: { all: "Tout", new: "Nouveau", saved: "Gardé" },
    detectedHint: "Trouvé par un audit, ou mis de côté",
    declined: "Refusé",
    tag: { technical: "Technique", content: "Contenu", schema: "Schema", performance: "Performance" },
    safety: { safe_draft: "Risque faible", review_required: "À relire", expert_required: "Expert requis", not_applicable: "Non applicable" },
    empty: "Rien ici.",
    more: (hidden) => `+ ${hidden} autres`,
    failedChip: "Arrêté",
    stoppedOnError: "Arrêtée sur une erreur",
    noErrorMessage: "Aucun message d'erreur n'a été enregistré.",
    manualChange: "Modification demandée",
    changeStatus: {
      requested: "En attente", planning: "Planification", editing: "Rédaction", validating: "Vérification", preview_ready: "Aperçu prêt",
      awaiting_approval: "À valider", approved: "Validé", deploying: "Publication", deployed: "En ligne",
      rejected: "Refusé", failed: "Échec", rolled_back: "Annulé",
    },
    updated: (ago) => `mis à jour ${ago}`,
    live: (ago) => `en ligne ${ago}`,
    allChecksPassed: "Tous les contrôles sont passés",
    blockedBy: (count) => `${count} ${count === 1 ? "contrôle demande" : "contrôles demandent"} une personne`,
  },
  action: {
    buildPreview: "Créer l'aperçu",
    save: "Garder",
    dismiss: "Écarter",
    approve: "Valider",
    reject: "Refuser",
    rollback: "Annuler",
    rollbackQuiet: "Annuler la mise en ligne…",
    retry: "Réessayer",
    details: "Voir le détail",
    openPreview: "Voir l'aperçu",
    confirm: "Confirmer",
    cancel: "Annuler",
    working: "En cours…",
    close: "Fermer",
    runAudit: "Lancer un audit",
    open: "Ouvrir",
    inspect: (name) => `Inspecter ${name}`,
  },
  notice: {
    approved: "Validé. La publication est en cours.",
    rejected: "Refusé. Rien n'a été publié.",
    rollbackStarted: "Annulation lancée. La version précédente est restaurée.",
    previewStarted: "Un aperçu est en construction. Rien n'est publié tant qu'il n'est pas validé.",
    saved: "Gardé pour plus tard.",
    dismissed: "Écarté. Un prochain audit peut le proposer à nouveau avec des preuves à jour.",
    auditRequested: "Audit demandé. Le résultat arrive bientôt.",
    batch: (approved, skipped) => `${approved} validée${approved > 1 ? "s" : ""}${skipped > 0 ? `, ${skipped} laissée${skipped > 1 ? "s" : ""} à une relecture individuelle` : ""}.`,
    reportApproved: "Rapport validé.",
    reportSuppressed: "Rapport retenu. Le client ne le verra pas.",
    failed: (message) => `Action impossible : ${message}`,
  },
  confirm: {
    rollback: "Annuler cette modification ? La version précédente repasse en ligne.",
    batch: (count) => `Valider ${count} ${count === 1 ? "modification" : "modifications"} ? Chacune garde son aperçu, ses preuves et son retour arrière.`,
  },
  batch: {
    ready: (count) => `${count} ${count === 1 ? "modification à faible risque est prête" : "modifications à faible risque sont prêtes"}`,
    approveAll: "Tout valider",
    explain: "Seules les modifications dont tous les contrôles sont passés et dont l'aperçu n'a pas changé sont incluses.",
    reason: {
      not_found: "N'existe plus",
      not_awaiting_approval: "Déjà décidée",
      no_recommendation: "Ne vient pas d'une recommandation",
      not_safe_class: "Demande une relecture humaine",
      not_applicable: "Ne peut pas être appliquée automatiquement",
      no_evidence: "Preuves manquantes",
      seo_findings: "Les contrôles SEO ont relevé des points",
      build_warnings: "La construction a émis des avertissements",
      broken_links_or_assets: "Liens cassés ou fichiers manquants",
      no_visual_evidence: "Pas de comparaison visuelle",
      visual_change_too_large: "La page change de façon visible",
      evidence_changed: "Les preuves ont changé depuis votre lecture",
    },
  },
  attention: {
    title: "À traiter",
    stopped: (ago) => `Arrêtée ${ago}`,
    incidents: (count) => `${count} ${count === 1 ? "incident ouvert" : "incidents ouverts"} dans le Parc`,
    manualHint: "Demande manuelle : le client la relance.",
  },
  fleet: {
    index: "Index du parc",
    allWebsites: "Tous les sites",
    search: "Rechercher un site, un domaine, un propriétaire…",
    filters: { all: "tous", critical: "critique", watch: "à surveiller", building: "en cours", healthy: "sain" },
    columns: { website: "Site", health: "Santé", work: "Travaux", seo: "SEO", usage: "Coût du mois", last: "Dernier signal", report: "Rapport" },
    noMatch: "Aucun site ne correspond.",
    clear: "Effacer les filtres",
    attention: "Intervention humaine",
    queue: "File d'attention",
    queueClear: "File vide",
    queueClearDetail: "Aucun site ne demande d'intervention.",
    take: "Prendre en charge",
    resolve: "Résoudre",
    ownedBy: (who) => `Pris en charge par ${who}`,
    you: "vous",
    systemTitle: "Système & activité",
    systemHint: (jobs, workflows) => `${jobs} ${jobs === 1 ? "tâche" : "tâches"} en cours · ${workflows} ${workflows === 1 ? "flux actif" : "flux actifs"}`,
    resourcesTitle: "Capacité et débit",
    resourcesDetail: "Inventaire opérationnel de tout le parc.",
    logTitle: "Activité récente",
    logDetail: "Derniers changements d'état de chaque ressource.",
    websites: "Sites",
    needAttention: "À surveiller",
    liveCount: (count) => `${count} en ligne`,
    incidents: (count) => `${count} ${count === 1 ? "incident ouvert" : "incidents ouverts"}`,
    activeWorkflows: "Traitements actifs",
    jobsRunning: (count) => `${count} ${count === 1 ? "tâche en cours" : "tâches en cours"}`,
    toApprove: (count) => `${count} à valider`,
    monthUsage: "Coût du mois",
    today: (amount) => `${amount} aujourd'hui`,
    approvals: "Attendent une validation",
    reviews: "Rapports à relire",
    workIdle: "Au repos",
    workActive: (count) => `${count} en cours`,
    scoreNone: "Pas évalué",
    scoreBehind: "Des modifications sont passées en ligne après cet audit",
    scoreCaptured: (ago) => `audit ${ago}`,
    recommendations: (count) => `${count} à décider`,
    footerSnapshot: "Instantané",
    resourceJobs: "Tâches en file",
    resourceDeployments: "Déploiements",
    resourceAudits: "Audits SEO",
    resourceRecommendations: "Recommandations",
    usageMonth: "cumul du mois",
    onboardButton: "Ajouter un site",
    onboardModal: {
      title: "Ajouter un site",
      subtitle: "Collez son adresse. Le premier audit démarre aussitôt et s'ouvre ici dès qu'il est prêt.",
      nameLabel: "Nom",
      namePlaceholder: "Acme Chauffage",
      nameHint: "Déduit de l'adresse. Modifiable.",
      urlLabel: "Adresse du site",
      urlPlaceholder: "acmechauffage.fr",
      urlInvalid: "Cela ne ressemble pas à une adresse de site.",
      goalLabel: "Qu'est-ce qui compte le plus ?",
      goals: {
        leads: "Prospects & Contacts",
        traffic: "Visibilité Recherche & IA",
        maintenance: "Maintenance & Économies",
      },
      goalHints: {
        leads: "Plus de prises de contact",
        traffic: "Trouvé sur Google et dans les réponses IA",
        maintenance: "Rapide, sûr, moins cher à faire tourner",
      },
      localeLabel: "Langue",
      ownerEmailLabel: "E-mail du propriétaire",
      ownerEmailPlaceholder: "contact@acmechauffage.fr",
      moreOptions: "Plus d'options",
      nextTitle: "La suite",
      next: ["Nous auditons le site en ligne, sans y toucher.", "Vous lisez l'audit et choisissez quoi améliorer.", "Chaque modification est prévisualisée. Rien n'est publié sans validation."],
      submitButton: "Ajouter et lancer l'audit",
      cancelButton: "Annuler",
      submitting: "Ajout…",
      successNotice: "Site ajouté. Le premier audit tourne et s'ouvrira ici dès qu'il est prêt.",
    },
  },
  activity: {
    kind: { job: "Tâche", deployment: "Déploiement", seo_audit: "Audit SEO", recommendation: "Recommandation", change_set: "Modification" },
    audit: { baseline: "Audit SEO de référence", weekly: "Audit SEO hebdomadaire", measurement: "Audit de mesure", manual: "Audit SEO à la demande" },
    environment: { preview: "Aperçu", production: "Production" },
    task: {
      light_audit: "Évaluation lancée",
      full_site_audit: "Audit complet du site",
      start_migration: "Migration lancée",
      crawl_site: "Site exploré",
      capture_page: "Page capturée",
      generate_project: "Site généré",
      build_project: "Site construit",
      deploy_preview: "Aperçu déployé",
      import_repository_baseline: "Dépôt importé",
    },
    status: {
      running: "en cours", pending: "en attente", completed: "terminé", failed: "échec", dead_lettered: "échec",
      active: "en ligne", superseded: "remplacé", building: "construction", publishing: "publication",
      queued: "en attente", partial: "partiel", rolled_back: "annulé", deployed: "en ligne",
      awaiting_approval: "à valider", rejected: "refusé", new: "nouveau", applied: "appliqué", measuring: "mesure en cours",
      measured: "mesuré", skipped: "écarté", previewing: "aperçu en construction", previewed: "aperçu prêt",
    },
  },
  health: { healthy: "Sain", building: "En cours", watch: "À surveiller", critical: "Critique" },
  drawer: {
    title: "Télémétrie du site",
    production: "Production",
    notConnected: "Non connecté",
    owner: "Propriétaire",
    systemOwned: "Géré par SiteAgain",
    live: "Version en ligne",
    lastChange: (ago) => `Dernier changement ${ago}`,
    noChange: "Rien n'a encore été publié",
    score: "Score SEO",
    scoreLine: (score, ago) => `${Math.round(score)} / 100 · audit ${ago}`,
    scoreBehind: "Des modifications sont passées en ligne après cet audit : le score peut ne pas les inclure. Un nouvel audit est demandé après chaque publication.",
    noScore: "Aucun audit terminé pour l'instant",
    changes: "Modifications",
    noChanges: "Aucune modification pour l'instant.",
    recommendations: "Recommandations",
    noRecommendations: "Aucune recommandation pour l'instant.",
    measurements: "Résultats mesurés",
    noMeasurements: "Rien n'a encore été mesuré. Un résultat apparaît 7, 14 et 28 jours après la mise en ligne.",
    audits: "Historique des audits",
    noAudits: "Aucun audit pour l'instant.",
    effects: "Après publication",
    noEffects: "Rien n'a été publié depuis l'enregistrement de ces suites.",
    reports: "Rapports mensuels",
    noReports: "Pas encore de rapport mensuel. Le premier est écrit après un mois civil complet.",
    search: "Search Console",
    noSearch: "Non connecté. Le client la connecte depuis sa vue Croissance.",
    searchStatus: { pending_property: "Choix de la propriété", active: "Connecté", needs_reconnect: "À reconnecter" },
    searchProperty: "Propriété",
    searchAccount: "Compte Google",
    searchThrough: (day) => `Chiffres jusqu'au ${day}`,
    searchNoNumbers: "Aucun chiffre récupéré pour l'instant",
    searchLastPull: (ago) => `Dernière récupération ${ago}`,
    searchRuns: "Dernières récupérations",
    searchNoRuns: "Rien de récupéré pour l'instant.",
    plan: {
      title: "Formule et build complet",
      none: "Aucune formule choisie. Le client la choisit après sa preuve rapide.",
      status: { requested: "Demandée", contacted: "Contacté", activated: "Active", cancelled: "Annulée" } as Record<string, string>,
      requestedAt: (when: string) => `Demandée ${when}`,
      activate: "Facturation en place : activer la formule",
      activateHint: "N'activez qu'une fois la facturation en place. Cela débloque le build complet de ce site.",
      scope: "Périmètre",
      fullSite: "Toutes les pages",
      homepage: "Page d'accueil seule",
      start: "Lancer le build complet",
      startHint: "Lance la migration complète avec toutes les vérifications qualité. Le site en ligne du client n'est pas touché.",
      latest: (status: string, when: string) => `Dernier build : ${status}, lancé ${when}`,
      running: "Un build est déjà en cours pour ce site.",
    },
    enquiries: "Demandes",
    noEnquiries: "Rien n'est encore arrivé par le formulaire de ce site. Le client le connecte depuis sa vue Croissance. Ce que les visiteurs écrivent appartient au client seul : les opérateurs ne voient que des totaux.",
    enquiryLast: "28 derniers jours",
    enquiryPrevious: "Les 28 jours d'avant",
    enquiryNoPrevious: "Pas encore à l'écoute pendant toute cette période",
    enquirySince: (day) => `Comptage depuis le ${day}`,
    enquiryLastAt: (ago) => `Dernière ${ago}`,
    enquiryEmailProblems: (failed, skipped) => [failed > 0 ? `${failed} e-mail${failed === 1 ? "" : "s"} au propriétaire en échec` : "", skipped > 0 ? `${skipped} sans e-mail` : ""].filter(Boolean).join(" · ") + " sur les 30 derniers jours",
    enquiryResults: "Mesuré face aux modifications",
    enquiryResult: (before, after) => `${before} → ${after} demandes`,
    enquiryVerdict: { improved: "Plus", neutral: "À peu près pareil", declined: "Moins", insufficient: "Pas assez" },
    runKind: { backfill: "Historique", refresh: "Actualisation" },
    runStatus: { running: "En cours", done: "Terminée", failed: "Échec" },
    runRows: (count) => `${count} lignes`,
    runTruncated: "limitée par le plafond de lignes",
    problem: {
      token_revoked: "Accès retiré par le client",
      access_lost: "Le compte Google n'a plus accès à la propriété",
      property_missing: "La propriété n'existe plus",
      provider_unavailable: "Google n'a pas répondu",
      rate_limited: "Google nous a demandé de ralentir",
      missing_scope: "L'autorisation de lecture n'a pas été accordée",
    },
    effectName: { refresh_audit: "Score actualisé", notify_owner: "Propriétaire prévenu", notify_search_engines: "Moteurs de recherche prévenus" },
    effectStatus: { pending: "En attente", running: "En cours", done: "Fait", skipped: "Inutile", failed: "Échec" },
    recommendationStatus: {
      new: "Nouvelle", viewed: "Vue", saved: "Gardée", skipped: "Écartée", previewing: "Aperçu en construction", previewed: "Aperçu prêt",
      applied: "En ligne", expired: "Expirée", invalidated: "Retirée", measuring: "Mesure en cours", measured: "Mesurée",
    },
    type: {
      metadata: "Titres et descriptions", schema: "Données structurées", internal_link: "Liens internes", content: "Contenu",
      technical: "Technique", performance: "Performance", structural: "Structure", new_page: "Nouvelle page", redirect: "Redirection",
    },
    loadFailed: "Impossible de charger ce site.",
  },
  reports: {
    title: "Rapports mensuels",
    subtitle: "Ce qui a été fait pour chaque site, mois civil par mois civil.",
    awaiting: "À relire",
    history: "Historique",
    none: "Aucun rapport mensuel n'a encore été écrit. Le premier apparaît après un mois civil complet.",
    autoApproval: (when) => `Si personne n'agit, il est validé et envoyé automatiquement à partir du ${when} (contrôle quotidien).`,
    approve: "Valider et envoyer",
    suppress: "Retenir",
    note: "Note (facultatif)",
    columns: { site: "Site", month: "Mois", status: "Statut", email: "Email", shipped: "En ligne", rolledBack: "Annulées", measured: "Mesurées" },
    status: { draft: "Brouillon", approved: "Validé", suppressed: "Retenu" },
    email: { pending: "Email en attente", sent: "Envoyé", skipped: "Non envoyé", failed: "Envoi échoué" },
    quiet: "Mois calme",
    reviewedBy: (who, when) => `Relu par ${who} ${when}`,
    autoReviewed: (when) => `Validé automatiquement ${when}`,
    loadFailed: "Impossible de charger le rapport.",
    review: "Relire",
    reviewHint: "Lisez le rapport ci-dessous tel que le client le verra. Valider envoie l'email aussitôt si le mois n'est pas calme.",
    sentTo: "Envoyé",
    quietNote: "Les mois calmes sont conservés et visibles dans l'app, mais jamais envoyés par email.",
    close: "Fermer le rapport",
    suppressConfirm: "Retenir ce rapport ? Le client ne le verra jamais.",
    noneAwaiting: "Rien n'attend de relecture.",
    withheldBy: (who, when) => `Retenu par ${who} ${when}`,
  },
  today: {
    greeting: { morning: "Bonjour", afternoon: "Bon après-midi", evening: "Bonsoir" },
    needsYou: (count) => (count === 0 ? "Rien ne vous attend pour l'instant." : `${count} ${count === 1 ? "chose vous attend" : "choses vous attendent"}.`),
    calm: "Audits, prévisualisations et mesures continuent en arrière-plan.",
    queueTitle: "À traiter",
    queueEmpty: "Vous êtes à jour.",
    kind: { stopped: "Arrêté sur une erreur", approve: "Prêt à valider", report: "Rapport mensuel à relire", audit: "Nouvel audit prêt" },
    auditReady: (count) => `${count} ${count === 1 ? "recommandation" : "recommandations"} à décider`,
    flowTitle: "Travail en cours",
    flowEmpty: "Rien à cette étape.",
    recentTitle: "Récemment",
    firstSiteTitle: "Ajoutez votre premier site",
    firstSiteBody: "Collez une adresse. SiteAgain l'audite, explique quoi améliorer, et rien n'est publié sans votre validation.",
    more: "Plus",
    showAll: (count) => `Tout afficher (${count})`,
  },
  audit: {
    eyebrow: "Audit SEO & GEO",
    back: "Retour",
    open: "Ouvrir l'audit",
    openReport: "Ouvrir l'audit",
    latest: "Dernier audit",
    history: "Audits précédents",
    running: "Audit en cours",
    runningBody: "En général une minute ou deux. Le rapport s'ouvre ici dès qu'il est prêt.",
    failed: "Cet audit n'a pas abouti",
    failedBody: "Aucun rapport n'a été enregistré. Relancez-en un ; le site en ligne n'est pas touché.",
    notAssessed: "Pas encore évalué",
    notAssessedBody: "Lancez un audit pour voir où en est ce site.",
    runAgain: "Lancer un nouvel audit",
    outOf: "/ 100",
    grade: (grade) => `Note ${grade}`,
    captured: (when) => `Capturé ${when}`,
    purpose: { baseline: "Audit de référence", weekly: "Audit hebdomadaire", measurement: "Audit de mesure", manual: "Audit à la demande" },
    partial: "Rapport partiel : certains contrôles n'ont pas répondu. Ce qui suit est ce qui a répondu.",
    components: "Détail du score",
    todo: "Prochaines étapes",
    todoHint: "Chaque étape est d'abord construite en prévisualisation privée. Rien n'est publié sans validation.",
    todoEmpty: "Cet audit n'a soulevé aucune recommandation. C'est un résultat valable.",
    findings: "Ce que l'audit a trouvé",
    findingsEmpty: "Aucun constat enregistré.",
    severity: { critical: "Critique", warning: "Avertissement", opportunity: "Opportunité", info: "Info" },
    impact: { high: "Impact fort", medium: "Impact moyen", low: "Impact faible" },
    remediation: "Comment corriger",
    affected: (count) => `${count} ${count === 1 ? "page" : "pages"}`,
    keywords: "Requêtes de recherche",
    keywordsHint: "Où le site apparaît aujourd'hui, selon le fournisseur.",
    columns: { query: "Requête", position: "Position", impressions: "Impressions", clicks: "Clics", volume: "Volume" },
    visibility: "Autorité & visibilité IA",
    aiCitation: "Part de citations IA",
    mentions: "Mentions IA",
    referringDomains: "Domaines référents",
    authority: "Autorité du domaine",
    rankedKeywords: "Mots-clés positionnés",
    limitations: "Ce que cet audit ne peut pas dire",
    loadFailed: "Impossible de charger cet audit.",
    ready: (site) => `L'audit de ${site} est prêt.`,
  },
  pulse: { aria: "Fraîcheur des données" },
  empty: { board: "Rien pour l'instant. Lancez un audit sur un site pour voir des recommandations ici." },
};

export function operatorCopy(locale: Locale): OperatorCopy {
  return locale === "fr" ? FR : EN;
}
